import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {WorkOrderService} from './work-order.service';
import {InvoiceDocumentService} from './invoice-document.service';
import {EmailService} from './email.service';
import {TransactionEntityService} from './transaction-entity.service';
import {WoInvoiceLine} from '../DataModels/workorder-model';

export interface WoInvoicingResult {
  success: boolean;
  invoiceId?: string;
  amount?: number;
  emailSent?: boolean;
  error?: string;
}

export interface WoInvoicingParams {
  workorderId: string;
  entityId?: string | null;
  customerEmail?: string | null;
  /** Work order number, used to override the invoice email subject/message. */
  woNumber?: string | null;
  /** Explicit lines to invoice. If omitted, all billable + not-yet-invoiced lines are billed. */
  lines?: WoInvoiceLine[];
  groupByType?: boolean;
}

/**
 * Shared work order invoicing logic — used by the WO billing tab (with an explicit user
 * selection) as well as any other entry point (e.g. bulk "bill completed work orders")
 * that simply wants to bill everything marked billable on a work order and email the
 * resulting invoice to the customer on file.
 */
@Injectable({providedIn: 'root'})
export class WoInvoicingService {

  constructor(
    private woService: WorkOrderService,
    private invoiceDocService: InvoiceDocumentService,
    private emailService: EmailService,
    private entityService: TransactionEntityService,
  ) {
  }

  /**
   * Generate an invoice for a work order and email it to the customer on file.
   * If `lines` is not provided, all billable, not-yet-invoiced hours/travel/materials
   * lines are billed (matching the "bill everything billable" default used outside
   * the billing tab).
   */
  async invoiceWorkOrder(params: WoInvoicingParams): Promise<WoInvoicingResult> {
    const {workorderId, entityId = null, customerEmail = null, woNumber = null, groupByType = true} = params;

    if (!workorderId) {
      return {success: false, error: 'Missing work order id.'};
    }

    try {
      let lines = params.lines;
      let amount: number | undefined;

      if (!lines) {
        const built = await this.buildAllBillableLines(workorderId, entityId);
        lines = built.lines;
        amount = built.amount;
        if (lines.length === 0) {
          return {success: false, error: 'No billable items to invoice.'};
        }
      }

      const res = await firstValueFrom(this.woService.generateInvoice(workorderId, lines, groupByType));

      let emailSent = false;
      if (res?.invoice_id && customerEmail) {
        try {
          await this.sendInvoiceEmail(res.invoice_id, customerEmail, woNumber, workorderId);
          emailSent = true;
        } catch {
          // Non-fatal — invoice was created, email prep/send failed silently
        }
      }

      return {success: true, invoiceId: res?.invoice_id, amount, emailSent};
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.error?.message ?? 'Error generating invoice.';
      return {success: false, error: msg};
    }
  }

  /** Loads the billing summary and builds an invoice line for every billable, un-invoiced item. */
  private async buildAllBillableLines(workorderId: string, entityId: string | null): Promise<{ lines: WoInvoiceLine[]; amount: number }> {
    const summary = await firstValueFrom(this.woService.getBillingSummary(workorderId));

    let entityHoursItemId: string | null = null;
    let entityTravelItemId: string | null = null;
    if (entityId) {
      try {
        const ctx = await firstValueFrom(this.entityService.loadEntityContext(entityId));
        if (ctx?.entity) {
          entityHoursItemId = (ctx.entity as any).workorder_hours_item_id ?? null;
          entityTravelItemId = (ctx.entity as any).workorder_travel_item_id ?? null;
        }
      } catch {
        // Non-fatal — item_id will fall back to blank
      }
    }

    const lines: WoInvoiceLine[] = [];
    let amount = 0;

    (summary?.hours ?? []).filter(l => l.billable && !l.invoiced).forEach(l => {
      lines.push({id: l.id, item_id: entityHoursItemId ?? ''});
      amount += l.billed_amount ?? (l.hours * l.billing_rate);
    });
    (summary?.travel ?? []).filter(l => l.billable && !l.invoiced).forEach(l => {
      lines.push({id: l.id, item_id: entityTravelItemId ?? ''});
      amount += l.billed_amount ?? (l.units * l.rate_per_unit);
    });
    (summary?.materials ?? []).filter(l => l.billable && !l.invoiced).forEach(l => {
      lines.push({id: l.id, item_id: l.item_id ?? ''});
      amount += l.billed_amount ?? (l.quantity * l.unit_price);
    });

    return {lines, amount: Math.round(amount * 100) / 100};
  }

  /** Builds the invoice email (matching invoice-form-ar's process) and sends it directly, no modal. */
  private async sendInvoiceEmail(invoiceId: string, toEmail: string, woNumber?: string | null, workorderId?: string | null): Promise<void> {
    const emailModel = await this.invoiceDocService.buildEmailModelById(invoiceId, 'ar');
    const invoiceNumber = emailModel.invoiceNumber ?? '';
    const woLabel = woNumber ?? workorderId ?? '';
    const subject = (invoiceNumber && woLabel)
      ? `Invoice ${invoiceNumber} has been created for Work Order ${woLabel}. Please review and remit payment at your earliest convenience.`
      : emailModel.Subject;
    this.emailService.sendEmail({
      FromEmail: emailModel.fromEmail,
      FromName: emailModel.fromName,
      ToEmail: toEmail,
      Subject: subject,
      Body: emailModel.body,
      PdfHtml: emailModel.pdfHtml,
      FileName: emailModel.FileName,
      IsBodyHtml: true,
      header_id: emailModel.header_id,
      entity_id: emailModel.entity_id,
      activity_message: emailModel.activity_message,
      includeAttachment: true,
      PrintOrientation: emailModel.print_orientation,
      ReportFormatType: emailModel.convert_attachment_to,
    }, true);
  }
}
